import { useCaptchaConfig } from "@casino/client/context/CaptchaConfig/CaptchaConfigContext";
import useResendingEnableTimeCookie from "@casino/legacy/client/components/2faManaging/hooks/useResendingEnableTimeCookie";
import useHandleLogin from "@casino/legacy/client/components/Auth/hooks/useHandleLogin";
import useRedirectUrl from "@casino/legacy/client/components/Auth/hooks/useRedirectUrl";
import useRedirectToLogin from "@casino/legacy/client/components/Auth/Register/RegisterBody/FormRegister/useFormRegister/useRedirectToLogin";
import hasNewDeviceError from "@casino/legacy/client/components/Auth/Shared/utils/hasNewDeviceError";
import hasSms2faError from "@casino/legacy/client/components/Auth/Shared/utils/hasSms2faError";
import setDataToGib from "@casino/legacy/client/components/Auth/Shared/utils/setDataToGib";
import socialLoginErrors from "@casino/legacy/client/components/Auth/Shared/utils/socialLoginErrors";
import toGenericErrors from "@casino/legacy/client/components/Auth/Shared/utils/toGenericErrors";
import useConnectionClicked from "@casino/legacy/client/features/SocialAuth/SocialAuthLogin/SocialAuthLoginView/ConnectionButton/hooks/useConnectionClicked";
import useForm from "@casino/legacy/client/hooks/form/useForm";
import useTranslation from "@casino/legacy/client/hooks/i18n/useTranslation";
import useRouter from "@casino/legacy/client/hooks/router/useRouter";
import usePhoneNumberHint from "@casino/legacy/client/hooks/usePhoneNumberHint";
import useLoginError from "@casino/legacy/client/hooks/user/useLoginError";
import useToast from "@casino/legacy/client/hooks/useToast";
import useAccountPopoverStore from "@casino/legacy/client/store/useAccountPopoverStore";
import deserializeErrors from "@casino/legacy/shared/utils/errors/deserializeErrors";
import nativeReplace from "@casino/legacy/shared/utils/router/nativeReplace";
import { getCookie, removeCookie } from "@workspace/cookies/browser";
import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type Reaptcha from "reaptcha";
import type FormValues from "./FormValues";
import useNewDeviceNotification from "./useNewDeviceNotification";
import useSms2fa from "./useSms2fa";

type Props = {
  isPopover?: boolean;
  onSuccess?: () => void;
  defaultValues?: {
    username?: string;
  };
};

const useFormLogin = ({ onSuccess, isPopover, defaultValues }: Props) => {
  const { reload } = useRouter();
  const { isLoginCaptchaRequired } = useCaptchaConfig();
  const { showNewDeviceNotification } = useNewDeviceNotification();
  const [loginSuccessful, setLoginSuccessful] = useState(false);
  const [captchaRequired, setCaptchaRequired] = useState(
    isLoginCaptchaRequired,
  );
  const redirect = useRedirectUrl();
  const { dismissPopover } = useAccountPopoverStore();
  const { handleLogin, loading } = useHandleLogin({ captchaRequired });
  const { error: loginError, setError, resetError } = useLoginError();
  const {
    formState,
    control,
    handleSubmit,
    errors,
    setErrors,
    clearErrors,
    setValue,
    getValues,
    register,
    required,
    watch,
  } = useForm<FormValues>({
    defaultValues: { uses2FA: false, username: defaultValues?.username },
  });
  const { setPhoneNumberHint, phoneHint } = usePhoneNumberHint();
  const { showToast } = useToast();
  const initialSocialAuthType = getCookie("social_auth_type");
  const [socialAuthType, setSocialAuthType] = useState(initialSocialAuthType);
  const [needsSms2fa, setNeedsSms2fa] = useState(false);
  const { setResendingEnableTimeCookie } = useResendingEnableTimeCookie();
  const handleConnectionClick = useConnectionClicked();
  const { showRedirectToLoginNotification } = useRedirectToLogin();

  const recaptchaRef = useRef<Reaptcha>(null);

  const resetNeedsSms2fa = useCallback(() => {
    setResendingEnableTimeCookie();
    setNeedsSms2fa(true);
  }, []);

  useEffect(() => {
    if (initialSocialAuthType) {
      setSocialAuthType(initialSocialAuthType);
    }
  }, [initialSocialAuthType]);
  useEffect(() => {
    if (socialAuthType && loginError.includes("SMS_2FA_")) {
      resetNeedsSms2fa();
    }
  }, [socialAuthType, loginError, resetNeedsSms2fa]);
  useEffect(() => {
    if (getCookie("existingEmail")) {
      showRedirectToLoginNotification();
      removeCookie("existingEmail");
    }
  }, [showRedirectToLoginNotification]);

  // Shows the error a previous attempt left in the `loginError` cookie (e.g. a
  // failed social login). Only this effect may call `setErrors` on its own:
  // `setErrors` re-renders the form, and an effect that also depends on the
  // router's functions (new on every render) would run again on every render.
  useEffect(() => {
    if (loginError && !socialLoginErrors.includes(loginError)) {
      setErrors([{ message: loginError }] as GenericErrorType[]);
    }
  }, [loginError, setErrors]);

  const redirectedAfterLogin = useRef(false);
  useEffect(() => {
    if (!loginSuccessful || redirectedAfterLogin.current) {
      return;
    }
    redirectedAfterLogin.current = true;
    if (isPopover) {
      dismissPopover(true);
      reload();
    } else {
      nativeReplace(redirect);
    }
  }, [isPopover, dismissPopover, loginSuccessful, redirect, reload]);

  const uses2FA = watch("uses2FA");
  const { isValid } = formState;
  const isSubmitDisabled = !isValid;

  const authenticatorLabelCheckbox = useTranslation(
    "profile.google_authenticator_checkbox",
  );
  const authenticatorLabelCode = useTranslation(
    "profile.google_authenticator_code",
  );

  const submit = async (values: FormValues) => {
    clearErrors();
    resetError();
    setSocialAuthType("");

    const payload = await handleLogin(values);
    if (payload.errors?.length) {
      const payloadErrors = toGenericErrors(payload.errors);
      setErrors(payloadErrors);
      setPhoneNumberHint(payloadErrors);
      const deserializedErrors = deserializeErrors(
        payloadErrors,
      ) as DeserializeError;
      if (hasNewDeviceError(deserializedErrors)) {
        showNewDeviceNotification();
      }
      const isOtp = !!deserializedErrors?.otp;
      const hasSms2fa = hasSms2faError(deserializedErrors);
      if (hasSms2fa) {
        setResendingEnableTimeCookie();
        setNeedsSms2fa(true);
      }
      if (!isOtp) {
        setNeedsSms2fa(false);
      }
      recaptchaRef?.current?.reset();
      if (deserializedErrors?.captcha) {
        setCaptchaRequired(true);
        setNeedsSms2fa(false);
      }
      setValue("uses2FA", uses2FA || (isOtp && !hasSms2fa && !needsSms2fa));
      return;
    }

    const userData = payload.userManagement;

    window.proto?.reset();

    setDataToGib(userData?.currentProfile?._id || "", payload.sessionId || "");

    onSuccess?.();
    setLoginSuccessful(true);
  };

  const formValues = getValues();

  const { authCodeErrorMessage } = useSms2fa(errors, submit, formValues);

  const handleCodeSubmit = (otp: string) => {
    if (loading || loginSuccessful) {
      return;
    }

    if (socialAuthType) {
      resetError();
      handleConnectionClick({
        handle: socialAuthType as Connection,
        otp,
        setOtpError,
      });
    } else {
      submit({ ...formValues, otp, uses2FA: true });
    }
  };
  const setOtpError = () => {
    setErrors([{ message: "error.SMS_2FA_IS_REQUIRED" }] as GenericErrorType[]);
    resetNeedsSms2fa();
  };

  const handleBlur = () => {
    clearErrors();
  };

  return {
    control,
    handleBlur,
    errors,
    uses2FA,
    loading: loading || loginSuccessful,
    isSubmitDisabled,
    clearErrors,
    register,
    required,
    captchaRequired,
    recaptchaRef: recaptchaRef as RefObject<Reaptcha>,
    authenticatorLabelCheckbox,
    authenticatorLabelCode,
    phoneHint,
    needsSms2fa,
    handleDialogClose: () => {
      clearErrors();
      resetError();
      setNeedsSms2fa(false);
      setSocialAuthType("");
    },
    handleCodeSubmit,
    authCodeErrorMessage,
    handleSubmit: handleSubmit(
      async (values) => {
        submit(values);
      },
      (values) => {
        if (values.otp?.message === "error.SMS_2FA_IS_REQUIRED") {
          setResendingEnableTimeCookie();
          setNeedsSms2fa(true);
        }
      },
    ),
    handleResendCodeClick: () => {
      handleCodeSubmit("");
      showToast({
        message: "profile.new_code_sent",
        timeout: 6000,
        variant: "success",
        position: "bottom",
      });
      setError("SMS_2FA_IS_REQUIRED");
    },
  };
};

export default useFormLogin;
